import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { EmployeeSearchMulti } from "./EmployeeSearch";

interface EditCycle {
  id: number;
  name: string;
  review_period_start: string;
  review_period_end: string;
  schedule_type: string;
  schedule_month: number | null;
  schedule_day: number | null;
  scope_type: string;
  scope_id: number | null;
  auto_assign_site_id: number | null;
  auto_assign_department_id: number | null;
  auto_assign_region_id: number | null;
  specific_employee_ids?: number[] | null;
  deadline_self_days: number;
  deadline_peer_days: number;
  deadline_manager_days: number;
  period_start_month: number | null;
  period_start_day: number | null;
  colleague_feedback_timing?: string;
  template_id?: number | null;
}

interface CycleFormProps {
  onSaved: () => void;
  onCancel: () => void;
  editCycle?: EditCycle | null;
}

interface ReviewTemplate {
  id: number;
  name: string;
  description?: string | null;
}

export default function CycleForm({ onSaved, onCancel, editCycle }: CycleFormProps) {
  const isEditing = !!editCycle;

  const [name, setName] = useState("");
  const [periodStart, setPeriodStart] = useState(new Date().toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString().slice(0, 10));
  const [scheduleType, setScheduleType] = useState("manual");
  const [scheduleMonth, setScheduleMonth] = useState<number>(1);
  const [scheduleDay, setScheduleDay] = useState<number>(1);
  const [periodStartMonth, setPeriodStartMonth] = useState<number>(1);
  const [periodStartDay, setPeriodStartDay] = useState<number>(1);
  const [scopeType, setScopeType] = useState("all");
  const [scopeId, setScopeId] = useState<string>("");
  const [deadlineSelf, setDeadlineSelf] = useState(7);
  const [deadlinePeer, setDeadlinePeer] = useState(14);
  const [deadlineManager, setDeadlineManager] = useState(21);
  const [colleagueFeedbackTiming, setColleagueFeedbackTiming] = useState("after_self_assessment");
  const [templateId, setTemplateId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sites, setSites] = useState<{ id: number; name: string }[]>([]);
  const [departments, setDepartments] = useState<{ id: number; name: string; site_name?: string | null }[]>([]);
  const [regions, setRegions] = useState<{ id: number; name: string; code: string }[]>([]);
  const [specificEmployeeIds, setSpecificEmployeeIds] = useState<number[]>([]);
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);

  // Load review templates on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data: ReviewTemplate[] = await api.get("/api/reviews/templates");
        if (!cancelled) setTemplates(data);
      } catch { /* template load failure is non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-fill form when editCycle is provided
  useEffect(() => {
    if (!editCycle) return;
    setName(editCycle.name);
    setPeriodStart(editCycle.review_period_start);
    setPeriodEnd(editCycle.review_period_end);
    setScheduleType(editCycle.schedule_type);
    setScheduleMonth(editCycle.schedule_month ?? 1);
    setScheduleDay(editCycle.schedule_day ?? 1);
    setPeriodStartMonth(editCycle.period_start_month ?? 1);
    setPeriodStartDay(editCycle.period_start_day ?? 1);
    setScopeType(editCycle.scope_type);
    setDeadlineSelf(editCycle.deadline_self_days);
    setDeadlinePeer(editCycle.deadline_peer_days);
    setDeadlineManager(editCycle.deadline_manager_days);
    setColleagueFeedbackTiming(editCycle.colleague_feedback_timing ?? "after_self_assessment");
    setTemplateId(editCycle.template_id != null ? String(editCycle.template_id) : "");
    setSpecificEmployeeIds(editCycle.specific_employee_ids ?? []);

    // Derive scopeId from the scope-specific ID fields
    if (editCycle.scope_type === "site" && editCycle.auto_assign_site_id != null) {
      setScopeId(String(editCycle.auto_assign_site_id));
    } else if (editCycle.scope_type === "department" && editCycle.auto_assign_department_id != null) {
      setScopeId(String(editCycle.auto_assign_department_id));
    } else if (editCycle.scope_type === "region" && editCycle.auto_assign_region_id != null) {
      setScopeId(String(editCycle.auto_assign_region_id));
    } else {
      setScopeId("");
    }
  }, [editCycle]);

  const loadScopeData = useCallback(async (scope: string) => {
    try {
      if (scope === "site" && sites.length === 0) setSites(await api.get("/api/sites"));
      if (scope === "department" && departments.length === 0) setDepartments(await api.get("/api/departments"));
      if (scope === "region" && regions.length === 0) setRegions(await api.get("/api/regions"));
    } catch { /* scope data load failure is non-fatal */ }
  }, [sites.length, departments.length, regions.length]);

  useEffect(() => {
    if (scopeType !== "all" && scopeType !== "specific_employees") {
      void loadScopeData(scopeType);
    }
  }, [scopeType, loadScopeData]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        name,
        review_period_start: periodStart,
        review_period_end: periodEnd,
        schedule_type: scheduleType,
        scheduled_month: scheduleType !== "manual" ? scheduleMonth : null,
        scheduled_day: scheduleType !== "manual" ? scheduleDay : null,
        auto_assign_to: scopeType === "all" ? "all_employees" : scopeType,
        auto_assign_site_id: scopeType === "site" && scopeId ? Number(scopeId) : null,
        auto_assign_department_id: scopeType === "department" && scopeId ? Number(scopeId) : null,
        auto_assign_region_id: scopeType === "region" && scopeId ? Number(scopeId) : null,
        self_assessment_deadline_days: deadlineSelf,
        colleague_feedback_deadline_days: deadlinePeer,
        manager_review_deadline_days: deadlineManager,
        period_start_month: scheduleType !== "manual" ? periodStartMonth : null,
        period_start_day: scheduleType !== "manual" ? periodStartDay : null,
        colleague_feedback_timing: colleagueFeedbackTiming,
        template_id: templateId ? Number(templateId) : null,
        ...(scopeType === "specific_employees" ? { specific_employee_ids: specificEmployeeIds } : {}),
      };

      if (isEditing) {
        await api.put(`/api/reviews/cycles/${editCycle.id}`, payload);
      } else {
        await api.post("/api/reviews/cycles", payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditing ? "update" : "create"} cycle`);
    } finally {
      setSubmitting(false);
    }
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">{isEditing ? "Edit Review Cycle" : "New Review Cycle"}</h3>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <label className="block text-sm">
        <span className="text-xs font-medium text-gray-700">Cycle Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Q3 2026 Performance Review" className="input mt-1" />
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Review Period Start</span>
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required className="input mt-1" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Review Period End</span>
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required className="input mt-1" />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Schedule Type</span>
          <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value)} className="input mt-1">
            <option value="manual">Manual</option>
            <option value="annual">Annual</option>
            <option value="semi_annual">Semi-Annual</option>
          </select>
        </label>
        {scheduleType !== "manual" && (
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-700">Auto-trigger Month</span>
              <select value={scheduleMonth} onChange={(e) => setScheduleMonth(Number(e.target.value))} className="input mt-1">
                {months.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-700">Auto-trigger Day</span>
              <select value={scheduleDay} onChange={(e) => setScheduleDay(Number(e.target.value))} className="input mt-1">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>
        )}
      </div>

      {scheduleType !== "manual" && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700">Period starts</p>
          <div className="grid grid-cols-2 gap-2">
            <select value={periodStartMonth} onChange={(e) => setPeriodStartMonth(Number(e.target.value))} className="input">
              {months.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={periodStartDay} onChange={(e) => setPeriodStartDay(Number(e.target.value))} className="input">
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-gray-500">
            {scheduleType === "annual"
              ? `Annual period: ${months[periodStartMonth - 1]} ${periodStartDay} → ${months[(periodStartMonth - 1 + 11) % 12]} (next year)`
              : `Semi-annual periods: ${months[periodStartMonth - 1]} ${periodStartDay} → ${months[(periodStartMonth + 4) % 12]}, then ${months[(periodStartMonth + 5) % 12]} ${periodStartDay} → ${months[(periodStartMonth - 1 + 11) % 12]}`
            }
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Scope</span>
          <select value={scopeType} onChange={(e) => { setScopeType(e.target.value); setScopeId(""); setSpecificEmployeeIds([]); }} className="input mt-1">
            <option value="all">All Employees</option>
            <option value="site">Site</option>
            <option value="department">Department</option>
            <option value="region">Region</option>
            <option value="specific_employees">Specific Employees</option>
          </select>
        </label>
        {scopeType === "site" && (
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Site</span>
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required className="input mt-1">
              <option value="">Select site...</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        {scopeType === "department" && (
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Department</span>
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required className="input mt-1">
              <option value="">Select department...</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}{d.site_name ? ` (${d.site_name})` : ""}</option>)}
            </select>
          </label>
        )}
        {scopeType === "region" && (
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Region</span>
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required className="input mt-1">
              <option value="">Select region...</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
            </select>
          </label>
        )}
      </div>

      {scopeType === "specific_employees" && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-700">Search Employees</label>
          <EmployeeSearchMulti
            values={specificEmployeeIds}
            onChange={(ids) => setSpecificEmployeeIds(ids)}
            placeholder="Search employees..."
          />
          {specificEmployeeIds.length === 0 && <p className="text-xs text-gray-400">No employees selected.</p>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Self-assessment deadline (days)</span>
          <input type="number" min={1} value={deadlineSelf} onChange={(e) => setDeadlineSelf(Number(e.target.value))} required className="input mt-1" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Colleague feedback deadline (days)</span>
          <input type="number" min={1} value={deadlinePeer} onChange={(e) => setDeadlinePeer(Number(e.target.value))} required className="input mt-1" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Manager review deadline (days)</span>
          <input type="number" min={1} value={deadlineManager} onChange={(e) => setDeadlineManager(Number(e.target.value))} required className="input mt-1" />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Colleague feedback timing</span>
          <select value={colleagueFeedbackTiming} onChange={(e) => setColleagueFeedbackTiming(e.target.value)} className="input mt-1">
            <option value="after_self_assessment">After employee submits self-assessment</option>
            <option value="parallel">In parallel with self-assessment</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Review Template</span>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="input mt-1">
            <option value="">None</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {submitting ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Changes" : "Create Cycle")}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}
